import React, { useMemo } from "react";

/**
 * Props:
 *  - start: "HH:MM" (24h) e.g. "10:00"
 *  - end:   "HH:MM" (24h) e.g. "20:30"
 *  - step: minutes (number) e.g. 30
 *  - value: current value (string)
 *  - onChange: (value) => void
 *  - placeholder: string
 *  - className: optional
 */
export default function DynamicTimeSelect({
  start = "10:00",
  end = "20:30",
  step = 30,
  value,
  onChange = () => {},
  placeholder = "Please select the time",
  className = "",
}) {
  // parse "HH:MM" -> minutes since midnight
  const parseToMinutes = (hhmm) => {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + (m || 0);
  };

  // format minutes since midnight -> "h:mm AM/PM"
  const formatTime = (mins) => {
    const hr24 = Math.floor(mins / 60);
    const minute = mins % 60;
    const suffix = hr24 >= 12 ? "PM" : "AM";
    let hr12 = hr24 % 12;
    if (hr12 === 0) hr12 = 12;
    const mm = minute === 0 ? "00" : String(minute).padStart(2, "0");
    return `${hr12}:${mm} ${suffix}`;
  };

  // generate times memoized
  const times = useMemo(() => {
    const out = [];
    const startMin = parseToMinutes(start);
    const endMin = parseToMinutes(end);

    if (isNaN(startMin) || isNaN(endMin) || step <= 0) return out;
    // include end if it lands exactly or is requested
    for (let m = startMin; m <= endMin; m += step) {
      out.push({ value: m, label: formatTime(m) }); // value as minutes (safer) and label readable
    }
    return out;
  }, [start, end, step]);

  return (
    <label className={`pd-label ${className}`}>
      <select
        className="pd-select"
        value={value ?? ""}
        onChange={(e) => {
          // here we pass back either minutes or label depending on preference
          // We'll return label (string) by default for compatibility with earlier code.
          onChange(e.target.value);
        }}
        required
      >
        <option value="">{placeholder}</option>

        {times.map((t) => (
          // using label string as option value for easy display; if you prefer numeric use t.value
          <option key={t.value} value={t.label}>
            {t.label}
          </option>
        ))}
      </select>
    </label>
  );
}
